import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";

/**
 * /kind-clear-deck — Coming Soon + Waitlist for the KIND & CLEAR deck
 * Tagline: "Boundaries, said with love."
 * Theme: Compassionate boundary scripts · Mind • Body • Spirit • Hygiene™ Tools
 * 44 cards · $2.22
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WAITLIST_GROUP = "kind-clear-deck-waitlist";

const BRAND = {
  teal: "#068fa2",
  tealDeep: "#0a5b6a",
  tealInk: "#042f2e",
  cream: "#efeae7",
  gold: "#d4af6a",
  orange: "#e8a06b",
};

// Visible card titles from the source PDF — sneak peek list
const SNEAK_PEEK_CARDS = [
  { category: "BEGIN", title: "BEGIN HERE" },
  { category: "MIND", title: "The Favor You Can't Give" },
  { category: "MIND", title: "The Invitation You Don't Want" },
  { category: "MIND", title: 'The "Can You Just\u2026"' },
  { category: "BODY", title: "The Repeat Borrower" },
  { category: "SPIRIT", title: "The Volunteer Pressure" },
  { category: "HYGIENE", title: "The Guilt Trip" },
];

export default function KindClearDeckPage() {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || status === "submitting") return;
    setStatus("submitting");
    setErrorMsg("");
    try {
      await axios.post(`${API_URL}/api/subscribe`, {
        email: email.trim().toLowerCase(),
        firstName: firstName.trim() || "Friend",
        group_id: WAITLIST_GROUP,
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err?.response?.data?.detail ||
          "Something hiccupped. Please try again in a moment."
      );
    }
  };

  return (
    <div
      data-testid="kind-clear-deck-page"
      style={{ backgroundColor: BRAND.cream, minHeight: "100vh", color: BRAND.tealInk }}
    >
      <Helmet>
        <title>KIND &amp; CLEAR Card Deck — Boundaries, Said With Love | Mind Body Spirit HYGIENE™</title>
        <meta
          name="description"
          content="44 compassionate boundary scripts for the moments you don't know what to say. The KIND & CLEAR card deck by Temple Franklin gives you the exact words for declining favors, ending guilt trips, and protecting your peace — said with love. $2.22 digital deck. Available now."
        />
        <meta
          name="keywords"
          content="boundary cards, boundary deck, kind boundaries, how to say no, boundary scripts, Mind Body Spirit HYGIENE™, Temple Franklin, boundary affirmations, compassionate boundaries"
        />
        <link rel="canonical" href="https://mindbodyspirithygienetools.com/kind-clear-deck" />
        <meta property="og:type" content="product" />
        <meta property="og:title" content="KIND &amp; CLEAR — Boundaries, Said With Love" />
        <meta
          property="og:description"
          content="44 compassionate boundary scripts for when you don't know what to say. Real words. Real grace. $2.22 digital deck. Available now."
        />
        <meta property="og:image" content="https://mindbodyspirithygienetools.com/kind-clear-deck/cover-card.png" />
        <meta property="og:url" content="https://mindbodyspirithygienetools.com/kind-clear-deck" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: "KIND & CLEAR — Boundary Card Deck",
            description:
              "44 compassionate boundary scripts. THE MOMENT → SAY THIS → WHY IT'S KIND. Digital download by Temple Franklin.",
            brand: { "@type": "Brand", name: "Mind Body Spirit HYGIENE\u2122" },
            image: "https://mindbodyspirithygienetools.com/kind-clear-deck/cover-card.png",
            offers: {
              "@type": "Offer",
              price: "2.22",
              priceCurrency: "USD",
              availability: "https://schema.org/InStock",
              url: "https://mindbodyspirithygienetools.com/kind-clear-deck",
            },
          })}
        </script>
      </Helmet>

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="px-6 pt-16 pb-12 md:pt-20 md:pb-16 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
          <div>
            <div
              data-testid="coming-soon-badge"
              className="inline-block uppercase text-xs md:text-sm px-5 py-2 rounded-full mb-6"
              style={{
                backgroundColor: BRAND.teal,
                color: BRAND.cream,
                letterSpacing: "0.3em",
              }}
            >
              ✦ Live Now — $2.22 ✦
            </div>

            <h1
              className="text-5xl sm:text-6xl lg:text-7xl mb-4 font-black tracking-tight"
              style={{ color: BRAND.tealDeep, letterSpacing: "-0.02em" }}
            >
              KIND &amp; CLEAR
            </h1>

            <div
              className="h-1 w-24 mb-5"
              style={{ backgroundColor: BRAND.gold }}
              aria-hidden="true"
            />

            <p
              className="text-2xl md:text-3xl italic mb-2"
              style={{ color: BRAND.tealDeep, fontFamily: "Georgia, serif" }}
            >
              Boundaries, said with love.
            </p>

            <p
              className="text-base md:text-lg italic mb-7 opacity-80"
              style={{ color: BRAND.tealDeep }}
            >
              Mind &middot; Body &middot; Spirit &middot; Hygiene&trade; Tools
            </p>

            <p
              className="text-base md:text-lg leading-relaxed mb-7"
              style={{ color: BRAND.tealInk }}
            >
              <strong>44 cards</strong> for the moments you don&rsquo;t know
              what to say. Each one names the situation, hands you the exact
              kind &amp; clear words, and reminds you why the boundary is
              loving &mdash; not mean. <em>You can love someone and still
              have no time.</em> These are the words for both.
            </p>

            <p
              className="text-sm md:text-base mb-2 uppercase tracking-[0.2em]"
              style={{ color: BRAND.gold }}
            >
              Launch price
            </p>
            <p
              className="text-5xl md:text-6xl mb-1"
              style={{ color: BRAND.tealDeep, fontFamily: "Georgia, serif", fontWeight: 700 }}
            >
              $2.22
            </p>
            <p className="text-sm italic mb-8" style={{ opacity: 0.7 }}>
              Digital download. Print at home or use on your phone.
            </p>

            <a
              href="https://templefranklin22.gumroad.com/l/gvkbv"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-buy-cta"
              className="inline-block px-8 py-4 rounded-md uppercase tracking-[0.2em] text-sm md:text-base transition-opacity hover:opacity-90"
              style={{
                backgroundColor: BRAND.tealDeep,
                color: BRAND.cream,
                fontWeight: 600,
              }}
            >
              Buy · $2.22 &rarr;
            </a>
          </div>

          <div className="flex justify-center">
            <div
              className="rounded-lg overflow-hidden shadow-2xl max-w-[380px] w-full"
              style={{ border: `1px solid ${BRAND.gold}50` }}
            >
              <img
                src="/kind-clear-deck/cover-card.png"
                alt="KIND & CLEAR deck cover card"
                className="w-full h-auto block"
                data-testid="kc-cover-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── 3-STEP FRAMEWORK ─────────────────────────────── */}
      <section
        className="px-6 py-16 md:py-20"
        style={{ backgroundColor: BRAND.tealDeep, color: BRAND.cream }}
      >
        <div className="max-w-3xl mx-auto text-center">
          <p
            className="uppercase tracking-[0.3em] text-xs mb-3"
            style={{ color: BRAND.gold }}
          >
            ✦ How every card works ✦
          </p>
          <h2
            className="text-3xl md:text-4xl mb-6"
            style={{ fontFamily: "Georgia, serif" }}
          >
            The exact words. The exact moment.
          </h2>
          <p className="text-base md:text-lg leading-relaxed opacity-95">
            No more freezing mid-conversation. No more guilt spiral after the
            fact. Each card walks you through the boundary in 3 short steps
            &mdash; so you can be kind <em>and</em> clear, without
            apologizing for your time.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mt-12 text-left">
            <div>
              <p
                className="text-3xl mb-2"
                style={{ color: BRAND.gold, fontFamily: "Georgia, serif" }}
              >
                01
              </p>
              <p
                className="uppercase tracking-[0.2em] text-xs mb-2"
                style={{ color: BRAND.gold }}
              >
                The Moment
              </p>
              <p className="text-sm opacity-90 leading-relaxed">
                The card names the exact situation you&rsquo;re in &mdash;
                so you stop second-guessing whether your &ldquo;no&rdquo; is
                valid.
              </p>
            </div>
            <div>
              <p
                className="text-3xl mb-2"
                style={{ color: BRAND.gold, fontFamily: "Georgia, serif" }}
              >
                02
              </p>
              <p
                className="uppercase tracking-[0.2em] text-xs mb-2"
                style={{ color: BRAND.gold }}
              >
                Say This
              </p>
              <p className="text-sm opacity-90 leading-relaxed">
                The literal script &mdash; word for word &mdash; you can use
                in person, in text, in DMs, at work, at the holiday table.
              </p>
            </div>
            <div>
              <p
                className="text-3xl mb-2"
                style={{ color: BRAND.gold, fontFamily: "Georgia, serif" }}
              >
                03
              </p>
              <p
                className="uppercase tracking-[0.2em] text-xs mb-2"
                style={{ color: BRAND.gold }}
              >
                Why It&rsquo;s Kind
              </p>
              <p className="text-sm opacity-90 leading-relaxed">
                The reframe that ends the guilt spiral &mdash; so you walk
                away knowing you protected the relationship, not damaged it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── SNEAK PEEK ───────────────────────────────────── */}
      <section className="px-6 py-16 max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <p
            className="uppercase tracking-[0.3em] text-xs mb-2"
            style={{ color: BRAND.gold }}
          >
            ✦ Sneak Peek ✦
          </p>
          <h2
            className="text-2xl md:text-3xl"
            style={{ fontFamily: "Georgia, serif", color: BRAND.tealDeep }}
          >
            A few of the 44 cards.
          </h2>
          <p className="text-sm md:text-base mt-3 italic" style={{ opacity: 0.7 }}>
            (The rest stay sealed until launch day.)
          </p>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          data-testid="sneak-peek-grid"
        >
          {SNEAK_PEEK_CARDS.map((c, i) => (
            <div
              key={c.title}
              data-testid={`peek-card-${i}`}
              className="rounded-lg overflow-hidden shadow-md transition-transform duration-300 hover:scale-[1.03] hover:shadow-xl"
              style={{
                backgroundColor: BRAND.teal,
                color: BRAND.cream,
                aspectRatio: "5 / 7",
                position: "relative",
              }}
            >
              <div
                className="absolute top-0 right-0 w-20 h-20 rounded-bl-full"
                style={{ backgroundColor: BRAND.tealInk }}
                aria-hidden="true"
              />
              <div className="p-5 h-full flex flex-col justify-between">
                <div>
                  <p className="text-xs tracking-[0.3em] opacity-90">
                    KIND &amp; CLEAR
                  </p>
                </div>
                <div>
                  <p
                    className="uppercase text-xs tracking-[0.3em] mb-2"
                    style={{ color: BRAND.gold }}
                  >
                    {c.category}
                  </p>
                  <p
                    className="text-xl md:text-2xl leading-tight"
                    style={{ fontFamily: "Georgia, serif" }}
                  >
                    {c.title}
                  </p>
                  <div
                    className="h-px mt-4"
                    style={{ backgroundColor: `${BRAND.gold}80` }}
                  />
                </div>
                <p className="text-xs opacity-75 italic">
                  Mind &middot; Body &middot; Spirit &middot; Hygiene&trade;
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── WAITLIST ─────────────────────────────────────── */}
      <section
        id="waitlist"
        className="px-6 py-16 md:py-20"
        style={{ backgroundColor: BRAND.cream }}
      >
        <div
          className="max-w-xl mx-auto p-8 md:p-12 rounded-lg text-center"
          style={{
            backgroundColor: BRAND.tealDeep,
            color: BRAND.cream,
            boxShadow: "0 20px 60px rgba(4,47,46,0.15)",
          }}
        >
          {status === "success" ? (
            <div data-testid="waitlist-success">
              <p
                className="uppercase tracking-[0.25em] text-xs mb-3"
                style={{ color: BRAND.gold }}
              >
                ✦ You&rsquo;re on the list ✦
              </p>
              <h3
                className="text-3xl md:text-4xl mb-5"
                style={{ fontFamily: "Georgia, serif" }}
              >
                Welcome home.
              </h3>
              <p className="text-base leading-relaxed opacity-90">
                I&rsquo;ll send the launch link the moment the deck goes live.
                First 100 on the list get a launch-day discount.
              </p>
              <p className="text-sm italic mt-6 opacity-75">&mdash; Temple</p>
            </div>
          ) : (
            <>
              <p
                className="uppercase tracking-[0.25em] text-xs mb-3"
                style={{ color: BRAND.gold }}
              >
                ✦ Join the waitlist ✦
              </p>
              <h3
                className="text-3xl md:text-4xl mb-3"
                style={{ fontFamily: "Georgia, serif" }}
              >
                Words you wish you had years ago.
              </h3>
              <p className="text-base mb-7 opacity-90 leading-relaxed">
                Launches NOW at <strong>$2.22</strong>. First 100 on
                the list get a launch-day discount.
              </p>

              <form
                onSubmit={handleSubmit}
                className="space-y-3"
                data-testid="waitlist-form"
              >
                <input
                  data-testid="waitlist-firstname"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name (optional)"
                  className="w-full px-5 py-3 rounded-md text-base"
                  style={{
                    backgroundColor: BRAND.cream,
                    color: BRAND.tealInk,
                    border: "none",
                  }}
                />
                <input
                  data-testid="waitlist-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  className="w-full px-5 py-3 rounded-md text-base"
                  style={{
                    backgroundColor: BRAND.cream,
                    color: BRAND.tealInk,
                    border: "none",
                  }}
                />
                <button
                  data-testid="waitlist-submit"
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full px-6 py-3 rounded-md text-base uppercase tracking-[0.2em] transition-opacity disabled:opacity-60"
                  style={{
                    backgroundColor: BRAND.gold,
                    color: BRAND.tealInk,
                    fontWeight: 600,
                  }}
                >
                  {status === "submitting"
                    ? "Adding you…"
                    : "Reserve my spot"}
                </button>
                {status === "error" && (
                  <p
                    data-testid="waitlist-error"
                    className="text-sm mt-2"
                    style={{ color: "#ffd6c2" }}
                  >
                    {errorMsg}
                  </p>
                )}
              </form>

              <p className="text-xs italic mt-5 opacity-70">
                No spam. Unsubscribe anytime. Just the launch + the discount.
              </p>
            </>
          )}
        </div>
      </section>

      {/* ── FOOTER LINKS ─────────────────────────────────── */}
      <section className="px-6 py-10 text-center text-sm" style={{ opacity: 0.75 }}>
        <p>
          Complete the trilogy:{" "}
          <Link
            to="/i-get-to-deck"
            data-testid="link-i-get-to-deck"
            style={{ color: BRAND.teal, textDecoration: "underline" }}
          >
            I GET TO Deck
          </Link>{" "}
          &middot;{" "}
          <Link
            to="/rewire-deck"
            data-testid="link-rewire-deck"
            style={{ color: BRAND.teal, textDecoration: "underline" }}
          >
            REWIRE Deck
          </Link>{" "}
          &middot;{" "}
          <Link
            to="/book"
            data-testid="link-book"
            style={{ color: BRAND.teal, textDecoration: "underline" }}
          >
            the book
          </Link>
          .
        </p>
        <p className="mt-3 italic" style={{ opacity: 0.7 }}>
          Mind Body Spirit HYGIENE&trade; &middot; mindbodyspirithygiene.com
        </p>
      </section>
    </div>
  );
}
